import { action, observable, makeObservable, computed, runInAction } from 'mobx';
import { persist } from 'mobx-persist';
import * as ReactDOMServer from 'react-dom/server';
import { isValidElement, ReactElement } from 'react';
import { appStore } from '../appStore';
import { LOCALES } from 'src/constants/main';
import { requests } from 'src/requests';
import client from 'src/services/client';
import cookies from 'src/services/cookie';
import sentry from 'src/services/sentry';
import wms from 'src/services/wms';
import { TLocale } from 'src/types/main';
import { getDefaultLanguage } from 'src/utils/helpers';
import logger from '~/services/logger';

export class LocaleStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return true;
  }
  @observable loading: boolean = false;
  @persist @observable language: TLocale = getDefaultLanguage(window.SETTINGS?.default_language);
  @persist @observable timestamp: null | number = null;
  @persist @observable isGetLocalizationFailed: boolean = false;
  @persist('object') @observable timestamps: { en: number | null; ar: number | null } = { en: null, ar: null };
  @persist('object') @observable locales: { en: any; ar: any } = { en: null, ar: null };

  @computed public get isRtlLanguage(): boolean {
    return [LOCALES.AR].includes(this.language);
  }

  @computed get localizationLoaded() {
    if (this.isGetLocalizationFailed) {
      return !this.loading;
    }
    return !this.loading && this.timestamp;
  }

  @action fetchLocale = async () => {
    try {
      this.loading = true;
      const locale = await requests.fetchLocales(appStore.settings?.translations_url as string, {
        language: this.language,
        timestamp: this.timestamp,
        segment: 'api',
      });
      if (!locale.relevance) {
        this.locales[this.language] = locale.translations;
        this.timestamps[this.language] = locale.timestamp;
        this.timestamp = locale.timestamp;
        this.isGetLocalizationFailed = false;
      }
    } catch (error: any) {
      this.isGetLocalizationFailed = true;
      logger.error({ error, place: 'LocaleStore fetchLocale' });
    } finally {
      this.loading = false;
    }
  };

  @action public setLocale(language: TLocale) {
    const isChanged = language !== this.language;
    cookies.setCookie('language', language);
    this.language = language;
    this.timestamp = this.timestamps[language];
    client.headers = {
      'Accept-Language': language,
    };

    if (isChanged && process.env.NODE_ENV !== 'test') {
      return window.location.reload();
    }

    this.fetchLocale();
  }

  @action public t = (key: string, options: Record<string, undefined | string | number | ReactElement> = {}): string => {
    if (!this.locales || !this.locales[this.language]) return key;

    let translated = this.locales[this.language][key] || key;

    for (const [name, value] of Object.entries(options)) {
      if (typeof value === 'string' || typeof value === 'number') {
        translated = translated.replace(`{{${name}}}`, String(value));
      } else if (isValidElement(value)) {
        translated = translated.replace(`{{${name}}}`, ReactDOMServer.renderToString(value));
      }
    }

    return translated;
  };

  afterHydration() {
    if (window.IS_MOBILE) {
      wms.setLanguage(this.language);
    }
    // set sentry locale tag
    sentry.tag('locale', this.language);

    cookies.setCookie('language', this.language);
    client.headers = {
      'Accept-Language': this.language,
    };
  }
}

const localeStore = new LocaleStore();

export { localeStore };
